import { useGridApiRef } from '@mui/x-data-grid-premium';
import { RiAddLine } from '@remixicon/react';
import { useCallback, useMemo, useState } from 'react';

import { AgButton } from 'src/components/ui';
import useFinanceConfiguration from 'src/hooks/useFinanceConfiguration';
import { useDeleteAccount, useDisableAccount } from 'src/modules/accounting/useAccountMutations';
import { LedgerAccountDetails, useLedgerAccounts } from 'src/modules/accounting/useLedgerAccounts';
import { useLedgerAccountsFragment } from 'src/modules/accounting/useLedgerAccountsFragment';
import { DataGrid, GridToolbarV2, useGridRows } from 'src/modules/grids';

import AccountDialog from './components/AccountDialog';
import useAccountColumns from './hooks/useAccountColumns';

const STORAGE_KEY = 'accounts_setting';

const rowSearchKeys = ['number', 'name'];

export type AccountWithBalance = LedgerAccountDetails & {
  balance: number | 'loading' | null;
  ownBalance: number | 'loading' | null;
  childBalance: number | 'loading' | null;
};

const getTreeDataPath = (row: AccountWithBalance) => {
  if (row.path) {
    return row.path.split('/');
  }
  return [row.name];
};

export default function AccountsTab() {
  const apiRef = useGridApiRef();
  const accounts = useLedgerAccounts({ includeDisabled: true });
  const fragmentAccounts = useLedgerAccountsFragment();
  const enhancedGridState = useGridRows({
    mode: 'local',
    storageKey: STORAGE_KEY,
    searchKeys: rowSearchKeys,
    getTreeDataPath,
  });

  const [editAccount, setEditAccount] = useState<Partial<LedgerAccountDetails> | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);

  const accountsWithBalance = useMemo<AccountWithBalance[]>(
    () =>
      accounts.map((acc) => {
        if (!fragmentAccounts.length) {
          return {
            ...acc,
            balance: 'loading' as const,
            ownBalance: 'loading' as const,
            childBalance: 'loading' as const,
          };
        }
        const fragmentRow = fragmentAccounts.find((a) => a.path === acc.path);
        if (!fragmentRow) {
          return { ...acc, balance: null, ownBalance: null, childBalance: null };
        }
        const ownBalance = fragmentRow?.ownBalance ?? 0;
        const childBalance = fragmentRow?.childBalance ?? 0;
        return { ...acc, balance: ownBalance + childBalance, ownBalance, childBalance };
      }),
    [accounts, fragmentAccounts]
  );

  const { disableAccount } = useDisableAccount();
  const { deleteAccount } = useDeleteAccount();

  const handleEditAccount = useCallback((account: Partial<LedgerAccountDetails>) => {
    setEditAccount(account);
    setDialogOpen(true);
  }, []);

  const handleDisableAccount = useCallback(
    (account: LedgerAccountDetails) => {
      // No onError on the hook: the shared Apollo error link toasts the failure; catch only stops the rejection.
      disableAccount({ variables: { id: account.id, disabled: !account.disabled } }).catch(() => null);
    },
    [disableAccount]
  );

  const handleDeleteAccount = useCallback(
    (account: LedgerAccountDetails) => {
      deleteAccount({ variables: { id: account.id } }).catch(() => null);
    },
    [deleteAccount]
  );

  const financeConfig = useFinanceConfiguration() as {
    defaultAssetsAccountId?: number | null;
    defaultLiabilitiesAccountId?: number | null;
    defaultExpensesAccountId?: number | null;
    defaultIncomeAccountId?: number | null;
  } | null;
  const defaultAccountIds = useMemo(() => {
    if (!financeConfig) return new Set<number>();
    return new Set<number>(
      [
        financeConfig.defaultAssetsAccountId,
        financeConfig.defaultLiabilitiesAccountId,
        financeConfig.defaultExpensesAccountId,
        financeConfig.defaultIncomeAccountId,
      ].filter((id): id is number => id != null)
    );
  }, [financeConfig]);

  const columns = useAccountColumns({
    onEdit: handleEditAccount,
    onDisable: handleDisableAccount,
    onDelete: handleDeleteAccount,
    accounts,
    defaultAccountIds,
  });

  const toolbar = useMemo(
    () => () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        entityName="Account"
        toolbarActionsRight={
          <AgButton variant="primary" onPress={() => setDialogOpen(true)}>
            <RiAddLine size={20} />
            Add Account
          </AgButton>
        }
      />
    ),
    [apiRef, enhancedGridState]
  );

  return (
    <>
      <DataGrid
        mode="local"
        rows={accountsWithBalance}
        enhancedGridState={enhancedGridState}
        columns={columns}
        gridKey="settings.accounting.accounts"
        storageKey={STORAGE_KEY}
        apiRef={apiRef}
        slots={{ toolbar }}
        variant="list"
        treeData
        getTreeDataPath={getTreeDataPath}
        defaultGroupingExpansionDepth={-1}
        groupingColDef={{
          headerName: 'Account',
          flex: 2,
          hideDescendantCount: true,
          valueFormatter: (_value, row) => (row.name ? `${row.number} ${row.name}` : ''),
          valueGetter: (_value, row) => row.number,
          sortComparator: (a, b) => {
            const numA = parseFloat(a) || 0;
            const numB = parseFloat(b) || 0;
            return numA - numB;
          },
          sortable: true,
        }}
        initialState={{ sorting: { sortModel: [{ field: '__tree_data_group__', sort: 'asc' }] } }}
      />
      <AccountDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditAccount(undefined);
        }}
        account={editAccount}
      />
    </>
  );
}
